/**
=========================================================
* Argon Dashboard 2 MUI - v3.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-material-ui
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components

import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import { CircularProgress } from "@mui/material";

// Argon Dashboard 2 MUI components
import ArgonBox from "components/ArgonBox";
import ArgonButton from "components/ArgonButton";
import ArgonInput from "components/ArgonInput";
import ArgonTypography from "components/ArgonTypography";

// Argon Dashboard 2 MUI examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";

import { useEffect, useState } from "react";

import AttendanceTableData from "Views/Activities/AllAttendance/data/AttendanceTableData";
import EditAttendanceModal from "Views/Activities/AllAttendance/components/EditAttendanceModal";
import DeleteDialog from "components/DeleteDialog/DeleteDialog";
import api from "utils/api";
import { toast } from "react-toastify";

const getSessionIdFromAttendance = (attendance = {}) => {
  const session = attendance.classSession || {};
  const rawSessionId =
    session.id ||
    session._id ||
    (typeof attendance.classSessionId === "object"
      ? attendance.classSessionId?._id
      : attendance.classSessionId) ||
    attendance.id ||
    attendance._id;

  return rawSessionId ? String(rawSessionId) : null;
};

const collectClassNameCandidates = (attendance = {}) => {
  const session = attendance.classSession || {};
  const values = new Set();
  const addCandidate = (value) => {
    if (value === undefined || value === null) {
      return;
    }

    const text = String(value).trim();
    if (text) {
      values.add(text);
    }
  };

  addCandidate(session.name);
  addCandidate(session.className);
  addCandidate(session.classTitle);
  addCandidate(session.courseName);
  addCandidate(session.programName);
  addCandidate(session.subject);
  addCandidate(session.subjectName);
  addCandidate(session.board);
  addCandidate(session.standard);
  addCandidate(session.batchName);
  addCandidate(attendance.topicCovered);
  addCandidate(attendance.subject);

  if (session.class) {
    if (typeof session.class === "string") {
      addCandidate(session.class);
    } else {
      addCandidate(session.class.name);
      addCandidate(session.class.className);
      addCandidate(session.class.title);
      addCandidate(session.class.standard);
    }
  }

  if (session.classId) {
    if (typeof session.classId === "string") {
      addCandidate(session.classId);
    } else {
      addCandidate(session.classId.name);
      addCandidate(session.classId.className);
      addCandidate(session.classId.title);
    }
  }

  if (session.course) {
    if (typeof session.course === "string") {
      addCandidate(session.course);
    } else {
      addCandidate(session.course.name);
      addCandidate(session.course.title);
    }
  }

  if (session.courseId) {
    if (typeof session.courseId === "string") {
      addCandidate(session.courseId);
    } else {
      addCandidate(session.courseId.name);
      addCandidate(session.courseId.title);
    }
  }

  if (session.program) {
    if (typeof session.program === "string") {
      addCandidate(session.program);
    } else {
      addCandidate(session.program.name);
      addCandidate(session.program.title);
    }
  }

  if (session.classInfo) {
    if (typeof session.classInfo === "string") {
      addCandidate(session.classInfo);
    } else {
      addCandidate(session.classInfo.name);
      addCandidate(session.classInfo.className);
    }
  }

  return Array.from(values);
};

const collectStudentNameCandidates = (attendance = {}) => {
  const values = new Set();
  const addCandidate = (value) => {
    if (value === undefined || value === null) {
      return;
    }

    const text = String(value).trim();
    if (text) {
      values.add(text);
    }
  };

  const appendStudent = (student = {}) => {
    addCandidate(student.name);
    addCandidate(student.fullName);
    addCandidate(student.studentName);
    addCandidate(student.childName);
    if (student.firstName || student.lastName) {
      addCandidate(`${student.firstName || ""} ${student.lastName || ""}`.trim());
    }
    addCandidate(student.email);
    addCandidate(student.username);
    addCandidate(student.userName);

    if (student.user) {
      addCandidate(student.user.name);
      if (student.user.firstName || student.user.lastName) {
        addCandidate(`${student.user.firstName || ""} ${student.user.lastName || ""}`.trim());
      }
    }

    if (student.profile) {
      addCandidate(student.profile.name);
      if (student.profile.firstName || student.profile.lastName) {
        addCandidate(`${student.profile.firstName || ""} ${student.profile.lastName || ""}`.trim());
      }
    }
  };

  (attendance.classSession?.students || []).forEach(appendStudent);

  if (Array.isArray(attendance.students)) {
    attendance.students.forEach(appendStudent);
  } else if (attendance.students && typeof attendance.students === "object") {
    appendStudent(attendance.students);
  }

  if (attendance.student && typeof attendance.student === "object") {
    appendStudent(attendance.student);
  } else {
    addCandidate(attendance.student);
    addCandidate(attendance.studentName);
  }

  return Array.from(values);
};

const computeFilteredAttendances = (source = [], classTermRaw = "", studentTermRaw = "") => {
  const classTerm = classTermRaw.trim().toLowerCase();
  const studentTerm = studentTermRaw.trim().toLowerCase();

  if (!classTerm && !studentTerm) {
    return source;
  }

  const matchingSessionIds = new Set();

  source.forEach((attendance) => {
    const sessionId = getSessionIdFromAttendance(attendance);
    if (!sessionId) {
      return;
    }

    const classMatches =
      !classTerm ||
      collectClassNameCandidates(attendance).some((candidate) =>
        candidate.toLowerCase().includes(classTerm)
      );

    const studentMatches =
      !studentTerm ||
      collectStudentNameCandidates(attendance).some((candidate) =>
        candidate.toLowerCase().includes(studentTerm)
      );

    if (classMatches && studentMatches) {
      matchingSessionIds.add(sessionId);
    }
  });

  if (!matchingSessionIds.size) {
    return [];
  }

  return source.filter((attendance) => {
    const sessionId = getSessionIdFromAttendance(attendance);
    return sessionId ? matchingSessionIds.has(sessionId) : false;
  });
};

function Tables() {
  const [loading, setLoading] = useState(false);
  const [attendanceData, setAttendanceData] = useState([]);
  const [filteredAttendanceData, setFilteredAttendanceData] = useState([]);
  const [searchClassName, setSearchClassName] = useState("");
  const [searchStudentName, setSearchStudentName] = useState("");

  //popup
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedAttendance, setSelectedAttendance] = useState(null);

  // Fetch attendance data with optional filters
  const fetchAttendance = async () => {
    setLoading(true);
    try {
      const response = await api.get("/attendance", {
        params: {
          sort: "createdAt",
          order: "desc",
        },
      });

      if (response.data.success) {
        const attendances = response.data.data || [];
        const enrichedAttendances = attendances.map((attendance) => {
          if (!attendance.classProgress && response.data.classProgress) {
            return {
              ...attendance,
              classProgress: response.data.classProgress,
            };
          }
          return attendance;
        });

        setAttendanceData(enrichedAttendances);
        setFilteredAttendanceData(
          computeFilteredAttendances(enrichedAttendances, searchClassName, searchStudentName)
        );
      }
    } catch (error) {
      console.error("Error fetching attendance:", error);
      toast.error(error.response?.data?.message || "Failed to fetch attendance data");
    } finally {
      setLoading(false);
    }
  };

  // Fetch all attendance data
  const fetchAllAttendance = () => {
    fetchAttendance();
  };

  // Delete button click handler
  const handleDeleteClick = () => {
    setDeleteDialog(true);
  };

  // Close dialog
  const handleCloseDialog = () => {
    setDeleteDialog(false);
  };

  // Edit button click handler
  const handleEditClick = (attendance) => {
    setSelectedAttendance(attendance);
    setEditModalOpen(true);
  };

  // Close edit modal
  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setSelectedAttendance(null);
  };

  // Handle attendance update
  const handleAttendanceUpdate = () => {
    fetchAllAttendance();
  };

  // Fetch data on mount
  useEffect(() => {
    fetchAllAttendance();
  }, []);

  const handleApplyFilters = () => {
    setFilteredAttendanceData(
      computeFilteredAttendances(attendanceData, searchClassName, searchStudentName)
    );
  };

  const handleResetFilters = () => {
    setSearchClassName("");
    setSearchStudentName("");
    setFilteredAttendanceData(attendanceData);
  };

  const { columns, rows } = AttendanceTableData(
    handleDeleteClick,
    handleEditClick,
    filteredAttendanceData
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <ArgonBox py={3}>
        <ArgonBox mb={3}>
          <Card>
            <ArgonBox p={3}>
              <ArgonTypography mb={2} variant="h6">
                Attendance List
              </ArgonTypography>

              <ArgonBox mb={3}>
                <Card
                  sx={{
                    boxShadow: "none",
                    border: "1px solid #e0e0e0",
                    borderRadius: "12px",
                    padding: 2,
                  }}
                >
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                      <ArgonInput
                        placeholder="Search by class name"
                        value={searchClassName}
                        onChange={(event) => setSearchClassName(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            event.preventDefault();
                            handleApplyFilters();
                          }
                        }}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <ArgonInput
                        placeholder="Search by student name"
                        value={searchStudentName}
                        onChange={(event) => setSearchStudentName(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            event.preventDefault();
                            handleApplyFilters();
                          }
                        }}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <ArgonBox display="flex" gap={1} justifyContent="end">
                        <ArgonButton
                          color="info"
                          variant="gradient"
                          onClick={handleApplyFilters}
                          disabled={loading}
                        >
                          Apply Filters
                        </ArgonButton>
                        <ArgonButton
                          color="secondary"
                          variant="outlined"
                          onClick={handleResetFilters}
                          disabled={loading}
                        >
                          Reset
                        </ArgonButton>
                      </ArgonBox>
                    </Grid>
                  </Grid>
                </Card>
              </ArgonBox>
            </ArgonBox>

            {loading ? (
              <ArgonBox display="flex" justifyContent="center" alignItems="center" p={5}>
                <CircularProgress />
              </ArgonBox>
            ) : (
              <ArgonBox
                sx={{
                  "& .MuiTableRow-root:not(:last-child)": {
                    "& td": {
                      borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                        `${borderWidth[1]} solid ${borderColor}`,
                    },
                  },
                }}
              >
                {rows.length > 0 ? (
                  <Table columns={columns} rows={rows} />
                ) : (
                  <ArgonBox textAlign="center" py={4}>
                    <ArgonTypography variant="h6" color="text" fontWeight="medium">
                      No attendance found
                    </ArgonTypography>
                    <ArgonTypography variant="caption" color="secondary">
                      {searchClassName || searchStudentName
                        ? "No attendance records match your search criteria. Try adjusting your filters."
                        : "No attendance records available at the moment."}
                    </ArgonTypography>
                  </ArgonBox>
                )}
              </ArgonBox>
            )}
          </Card>
        </ArgonBox>

        <DeleteDialog
          open={deleteDialog}
          onClose={handleCloseDialog}
          onConfirm={handleCloseDialog}
          title="Delete Attendance"
          description="Are you sure you want to delete this attendance?"
          confirmText="Delete"
          cancelText="Cancel"
        />

        <EditAttendanceModal
          open={editModalOpen}
          onClose={handleCloseEditModal}
          attendance={selectedAttendance}
          onUpdate={handleAttendanceUpdate}
        />
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Tables;
